import { useState } from "react";
import { useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { isDemoMode, MFA_SETUP_PATH, FAKE_REST_MFA_ENROLLED_KEY, FAKE_REST_MFA_TRUSTED_KEY } from "./constants";
import {
  getStoredTrustedToken,
  revokeTrustedDevices,
  revokeTrustedDevicesForFactor,
} from "./trustedDevice";
import { useMfaStatus } from "./useMfaStatus";

export const MfaSettingsSection = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const { loading, factors, refresh } = useMfaStatus();
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [forgettingDevice, setForgettingDevice] = useState(false);
  const [trustedDeviceForgotten, setTrustedDeviceForgotten] = useState(false);
  const hasTrustedDevice =
    !trustedDeviceForgotten &&
    (isDemoMode()
      ? localStorage.getItem(FAKE_REST_MFA_TRUSTED_KEY) === "true"
      : getStoredTrustedToken() != null);

  const verifiedFactors = [...factors.totp, ...factors.phone].filter(
    (factor) => factor.status === "verified",
  );

  const handleForgetTrustedDevice = async () => {
    if (isDemoMode()) {
      localStorage.removeItem(FAKE_REST_MFA_TRUSTED_KEY);
      setTrustedDeviceForgotten(true);
      notify("crm.mfa.trusted_device_forgotten", {
        type: "success",
        messageArgs: {
          _: "This browser will ask for a verification code on next sign-in.",
        },
      });
      return;
    }

    setForgettingDevice(true);
    try {
      await revokeTrustedDevices();
      setTrustedDeviceForgotten(true);
      notify("crm.mfa.trusted_device_forgotten", {
        type: "success",
        messageArgs: {
          _: "This browser will ask for a verification code on next sign-in.",
        },
      });
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.trusted_device_error", {
        type: "error",
        messageArgs: {
          _: "Could not forget this trusted device. Try again.",
        },
      });
    } finally {
      setForgettingDevice(false);
    }
  };

  const handleUnenroll = async (factorId: string) => {
    if (isDemoMode()) {
      localStorage.removeItem(FAKE_REST_MFA_ENROLLED_KEY);
      localStorage.removeItem(FAKE_REST_MFA_TRUSTED_KEY);
      notify("crm.mfa.factor_removed", {
        type: "success",
        messageArgs: { _: "MFA factor removed (demo)." },
      });
      await refresh();
      return;
    }

    setRemovingId(factorId);
    try {
      const { error } = await getSupabaseClient().auth.mfa.unenroll({
        factorId,
      });
      if (error) {
        throw error;
      }
      await revokeTrustedDevicesForFactor(factorId);
      notify("crm.mfa.factor_removed", {
        type: "success",
        messageArgs: { _: "MFA factor removed." },
      });
      await refresh();
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.unenroll_error", {
        type: "error",
        messageArgs: {
          _: "Could not remove MFA factor. Try again.",
        },
      });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>
          {translate("crm.mfa.settings.title", {
            _: "Two-factor authentication",
          })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {translate("crm.mfa.settings.description", {
            _: "Manage your MFA factors. We recommend keeping a backup authenticator app.",
          })}
        </p>

        {loading ? (
          <p className="text-sm">{translate("crm.common.loading")}</p>
        ) : verifiedFactors.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.mfa.settings.none", {
              _: "No MFA factors enrolled.",
            })}
          </p>
        ) : (
          <ul className="space-y-2">
            {verifiedFactors.map((factor) => (
              <li
                key={factor.id}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span>
                  {"factor_type" in factor && factor.factor_type === "phone"
                    ? translate("crm.mfa.sms.friendly_name", { _: "SMS" })
                    : translate("crm.mfa.totp.friendly_name", {
                        _: "Authenticator app",
                      })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={removingId === factor.id}
                  onClick={() => handleUnenroll(factor.id)}
                >
                  {translate("crm.mfa.settings.remove", { _: "Remove" })}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(MFA_SETUP_PATH)}
        >
          {translate("crm.mfa.settings.add_factor", {
            _: "Add backup factor",
          })}
        </Button>

        {hasTrustedDevice ? (
          <Button
            type="button"
            variant="outline"
            disabled={forgettingDevice}
            onClick={() => void handleForgetTrustedDevice()}
          >
            {translate("crm.mfa.settings.forget_device", {
              _: "Forget trusted device on this browser",
            })}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
};
